import { useReactiveVar } from '@apollo/client'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined'
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined'
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import MessageOutlinedIcon from '@mui/icons-material/MessageOutlined'
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import ReceiptOutlinedIcon from '@mui/icons-material/ReceiptOutlined'
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined'
import ScienceIcon from '@mui/icons-material/Science'
import SecurityIcon from '@mui/icons-material/Security'
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined'
import { useFlagsStatus } from '@unleash/proxy-client-react'
import {
  CoachesBillingDashboardResource,
  CoachesBillingResource
} from 'coachesBillingResource.types'
import { AuthorizedFeature, Loader } from 'components'
import {
  resetCondensedEventCardsVar,
  resetEventFilters,
  dateRangeDropDownQueryParamsVar
} from 'context'
import { useAuthorizedFeature } from 'hooks'
import { currentUserId } from 'hooks/useInvalidateQueriesOnUserChange'
import { NutrisenseShortLogo } from 'icons/NutrisenseShortLogo'
import { LocationPaths } from 'location.types'
import { startCase } from 'lodash-es'
import { generatePath } from 'react-router'
import { AdminPanelSidebarIcon } from 'screens/AdminPanel/AdminPanelSidebarIcon'
import { ChatbotAssistantSidebarItem } from 'screens/Chatbot/Assistant/ChatbotAssistantSidebarItem'
import { ChatbotMessageReportsSidebarIcon } from 'screens/ChatbotMessageReports/ChatbotMessageReportsSidebarIcon'
import { QuestionnaireSidebarIcon } from 'screens/Questionnaire/QuestionnaireSidebarIcon'
import { UserResource } from 'userResource.types'
import { FeatureGrant, useCurrentAdminQuery, useCurrentUserQuery } from 'types'
import { SidebarItem } from './SidebarItem'
import { SidebarNavStyled, SidebarTop, SidebarBottom } from './styles'

interface ISidebarNavigationProps {
  isAssistantOpen: boolean
  onToggleAssistant: () => void
}

export function SidebarNavigation({ isAssistantOpen, onToggleAssistant }: ISidebarNavigationProps) {
  const { data: { currentUser } = {} } = useCurrentUserQuery()
  const { data: { currentAdmin } = {} } = useCurrentAdminQuery()

  const id = currentUserId || currentUser?.id

  const [coachesBillingDashboardAuthorized, coachesBillingDetailsAuthorized] = useAuthorizedFeature(
    [FeatureGrant.CoachesBillingDashboard, FeatureGrant.CoachesBillingDetails]
  )

  const { flagsReady } = useFlagsStatus()

  let dateRangeDropDownQueryParams = useReactiveVar(dateRangeDropDownQueryParamsVar)
  dateRangeDropDownQueryParams = dateRangeDropDownQueryParams
    ? `?${dateRangeDropDownQueryParams}`
    : ''

  const userPath = `${LocationPaths.Users}/${id}`

  const coachInvoiceDetailsPath =
    currentAdmin && `${LocationPaths.Coaches}/${currentAdmin.id}/billing/invoices/items`

  const navLinks = (
    <>
      <SidebarTop>
        <AuthorizedFeature feature={FeatureGrant.OrganizationMembers}>
          <SidebarItem to={LocationPaths.OrganizationMembers} tooltipTitle="Home">
            <HomeOutlinedIcon />
          </SidebarItem>
        </AuthorizedFeature>

        <SidebarItem
          end
          to={`${userPath}${dateRangeDropDownQueryParams}`}
          tooltipTitle="Profile"
          onClick={() => {
            resetEventFilters()
            resetCondensedEventCardsVar()
          }}
        >
          <PeopleOutlinedIcon />
        </SidebarItem>

        <SidebarItem
          to={`${userPath}/${UserResource.History}${dateRangeDropDownQueryParams}`}
          tooltipTitle={startCase(UserResource.History)}
          onClick={resetEventFilters}
        >
          <AccessTimeIcon />
        </SidebarItem>

        <AuthorizedFeature feature={FeatureGrant.Chat}>
          <SidebarItem
            to={`${userPath}/${UserResource.Chat}`}
            tooltipTitle={startCase(UserResource.Chat)}
          >
            <MessageOutlinedIcon />
          </SidebarItem>
        </AuthorizedFeature>

        <AuthorizedFeature feature={FeatureGrant.BillingDashboard}>
          <SidebarItem
            to={`${userPath}/${UserResource.BillingDashboard}`}
            tooltipTitle="Billing Dashboard"
          >
            <CreditCardOutlinedIcon />
          </SidebarItem>
        </AuthorizedFeature>

        <SidebarItem
          to={`${userPath}/${UserResource.Insights}${dateRangeDropDownQueryParams}`}
          tooltipTitle={startCase(UserResource.Insights)}
        >
          <TrendingUpOutlinedIcon />
        </SidebarItem>

        <AuthorizedFeature feature={FeatureGrant.Questionnaire}>
          <SidebarItem
            to={`${userPath}/${UserResource.Questionnaire}`}
            tooltipTitle={startCase(UserResource.Questionnaire)}
          >
            <QuestionnaireSidebarIcon />
          </SidebarItem>
        </AuthorizedFeature>

        <AuthorizedFeature feature={FeatureGrant.Files}>
          <SidebarItem
            to={`${userPath}/${UserResource.Files}`}
            tooltipTitle={startCase(UserResource.Files)}
          >
            <FolderOpenOutlinedIcon />
          </SidebarItem>
        </AuthorizedFeature>

        <SidebarItem
          to={`${userPath}/${UserResource.Chatbot}`}
          tooltipTitle={startCase(UserResource.Chatbot)}
        >
          <AutoAwesomeIcon />
        </SidebarItem>

        <AuthorizedFeature feature={FeatureGrant.ChatbotViewKnowledgeBase}>
          <SidebarItem to={LocationPaths.Knowledge} tooltipTitle="Knowledge Base">
            <MenuBookIcon />
          </SidebarItem>
        </AuthorizedFeature>

        <AuthorizedFeature feature={FeatureGrant.Financials}>
          <SidebarItem
            to={LocationPaths.Financials}
            tooltipTitle={startCase(LocationPaths.Financials)}
          >
            <AttachMoneyIcon />
          </SidebarItem>
        </AuthorizedFeature>

        <AuthorizedFeature feature={FeatureGrant.SensorsReplacementRequests}>
          <SidebarItem
            to={LocationPaths.ReplacementRequests}
            tooltipTitle={startCase(LocationPaths.ReplacementRequests)}
          >
            <ReplayOutlinedIcon />
          </SidebarItem>
        </AuthorizedFeature>

        <AuthorizedFeature feature={FeatureGrant.ChatbotMessageReports}>
          <SidebarItem
            to={LocationPaths.ChatbotMessageReports}
            tooltipTitle={startCase(LocationPaths.ChatbotMessageReports)}
          >
            <ChatbotMessageReportsSidebarIcon />
          </SidebarItem>
        </AuthorizedFeature>

        <AuthorizedFeature feature={FeatureGrant.ShipmentsViewUser}>
          <SidebarItem
            to={`${userPath}/${UserResource.Shipments}`}
            tooltipTitle={startCase(UserResource.Shipments)}
          >
            <LocalShippingIcon />
          </SidebarItem>
        </AuthorizedFeature>

        <AuthorizedFeature feature={FeatureGrant.AdminPanelMembers}>
          <SidebarItem to={LocationPaths.AdminPanel} tooltipTitle="Admin Panel">
            <AdminPanelSidebarIcon />
          </SidebarItem>
        </AuthorizedFeature>

        <AuthorizedFeature feature={FeatureGrant.ViewAppointments}>
          <SidebarItem to={`${userPath}/${UserResource.Appointments}`} tooltipTitle="Appointments">
            <CalendarTodayOutlinedIcon />
          </SidebarItem>
        </AuthorizedFeature>

        <AuthorizedFeature feature={FeatureGrant.SurveysManagement}>
          <SidebarItem to={LocationPaths.Surveys} tooltipTitle="Surveys">
            <FactCheckOutlinedIcon />
          </SidebarItem>
        </AuthorizedFeature>

        {/* We migrated out from Health Portal Tickets to Kustomer. As it's a recent
        change, I'm leaving this code commented in the case anything changes.
        I will revisit it in 12/15/2025 */}
        {/* <AuthorizedFeature feature={FeatureGrant.SupportTickets}>
          <SidebarItem to={LocationPaths.SupportTickets} tooltipTitle="Support Tickets">
            <InboxOutlinedIcon />
          </SidebarItem>
        </AuthorizedFeature> */}

        <AuthorizedFeature feature={FeatureGrant.SupportAccountRequests}>
          <SidebarItem
            to={LocationPaths.AccountRequests}
            tooltipTitle="Account Processes Dashboard"
          >
            <ReceiptOutlinedIcon />
          </SidebarItem>
        </AuthorizedFeature>
        <AuthorizedFeature feature={FeatureGrant.ViewHealthTeamStructure}>
          <SidebarItem to={LocationPaths.NutritionistsOrganization} tooltipTitle="Health Team">
            <AdminPanelSettingsOutlinedIcon />
          </SidebarItem>
        </AuthorizedFeature>

        {coachesBillingDashboardAuthorized && (
          <SidebarItem
            to={`${LocationPaths.Coaches}/${CoachesBillingResource.BillingDashboard}/${CoachesBillingDashboardResource.Users}`}
            tooltipTitle="Coach Billing Dashboard"
          >
            <AccountBalanceOutlinedIcon />
          </SidebarItem>
        )}
        {!coachesBillingDashboardAuthorized &&
          coachesBillingDetailsAuthorized &&
          coachInvoiceDetailsPath && (
            <SidebarItem to={coachInvoiceDetailsPath} tooltipTitle="Coach Invoice Details">
              <AccountBalanceOutlinedIcon />
            </SidebarItem>
          )}
        {/* <AuthorizedFeature feature={FeatureGrant.AuthorizationManagement}>
          <SidebarItem to={LocationPaths.SupportTickets} tooltipTitle="Support Tickets">
            <InboxOutlinedIcon />
          </SidebarItem>
        </AuthorizedFeature> */}
        <AuthorizedFeature feature={FeatureGrant.AuthorizationManagement}>
          <SidebarItem
            to={`${userPath}/${UserResource.Authorization}`}
            tooltipTitle="Authorization Management"
          >
            <SecurityIcon />
          </SidebarItem>
        </AuthorizedFeature>
        <AuthorizedFeature feature={FeatureGrant.Formulas}>
          <SidebarItem
            to={`${userPath}/${generatePath(UserResource.Formulas)}`}
            tooltipTitle="Formulas"
          >
            <ScienceIcon />
          </SidebarItem>
        </AuthorizedFeature>
      </SidebarTop>
      <SidebarBottom>
        <ChatbotAssistantSidebarItem active={isAssistantOpen} onClick={onToggleAssistant} />
      </SidebarBottom>
    </>
  )

  return (
    <SidebarNavStyled>
      <NutrisenseShortLogo sx={{ width: 36, height: 36, alignSelf: 'center' }} />
      {flagsReady ? navLinks : <Loader />}
    </SidebarNavStyled>
  )
}
